import { tWithKeyAttr as t } from "../i18nHelper";
import {
  CreditType,
  FeatureCreditPackageDto,
  FeatureOption,
} from "../services/api/featureCreditPackagesApi";

// Display label for a feature option in the package create/edit select.
// Derived from creditType: token → "AI Credits"; seat → the patient/therapist
// label (which seat differs by featureKey). Anything else falls back to the
// generic creditType label.
export const featureOptionLabel = (option: FeatureOption): string => {
  if (option.creditType === "token") return t("FEATURE_OPTION_AI_CREDITS");
  if (option.creditType === "seat") {
    switch (option.featureKey) {
      case "MAX_PATIENTS":
        return t("FEATURE_OPTION_PATIENT_SEATS");
      case "MAX_THERAPISTS":
        return t("FEATURE_OPTION_THERAPIST_SEATS");
      default:
        return creditTypeLabel("seat");
    }
  }
  return creditTypeLabel(option.creditType);
};

// Human-readable label for the creditType badge.
export const creditTypeLabel = (creditType: CreditType): string => {
  switch (creditType) {
    case "token":
      return t("CREDIT_TYPE_TOKEN");
    case "seat":
      return t("CREDIT_TYPE_SEAT");
    case "count":
      return t("CREDIT_TYPE_COUNT");
    default:
      return creditType;
  }
};

// Suggested unit for a pack's size field, by creditType.
export const amountUnitLabel = (creditType?: CreditType | null): string => {
  switch (creditType) {
    case "token":
      return t("AMOUNT_UNIT_CREDITS");
    case "seat":
      return t("AMOUNT_UNIT_SEATS");
    case "count":
      return t("AMOUNT_UNIT_USES");
    default:
      return "";
  }
};

// Credits are decimal(18,4), so 0.2 and 1234.5678 are both valid — don't let the default
// three-digit rounding of toLocaleString() quietly change a configured figure.
export const formatCredits = (value: number): string =>
  value.toLocaleString(undefined, { maximumFractionDigits: 4 });

// How much a pack grants. Token packs are credit-denominated (`creditsGranted`, `amount` is null);
// count and seat packs are not, and carry their size in `amount`.
const packageSize = (pkg: FeatureCreditPackageDto): number | null =>
  pkg.creditType === "token" ? pkg.creditsGranted : pkg.amount;

// "200 credits" / "10 uses" / "5 seats" — the one place a pack's size is turned into copy.
export const packageSizeLabel = (pkg: FeatureCreditPackageDto): string => {
  const size = packageSize(pkg);
  if (size == null) return "-";
  return `${formatCredits(size)} ${amountUnitLabel(pkg.creditType)}`;
};

// How much a purchase granted, as reported by both a purchase-history row and a Buy response — a
// token grant carries its size in creditsGranted, a count/seat grant in amountGranted, and the
// other field is null. Only the three fields the split needs, so both DTOs satisfy it.
export interface CreditGrant {
  creditType: CreditType;
  amountGranted: number | null;
  creditsGranted: number | null;
}

// Same amount/creditsGranted split as packageSize, plus a purchase can legitimately have neither set
// yet (e.g. a Pending/Failed row that never granted anything).
const purchaseAmount = (purchase: CreditGrant): number | null =>
  purchase.creditType === "token" ? purchase.creditsGranted : purchase.amountGranted;

// "200 credits" / "10 uses" / "5 seats" for a purchase-history row — see packageSizeLabel.
export const purchaseAmountLabel = (purchase: CreditGrant): string => {
  const size = purchaseAmount(purchase);
  if (size == null) return "-";
  return `${formatCredits(size)} ${amountUnitLabel(purchase.creditType)}`;
};

const getServerErrorCode = (error: unknown): string | undefined =>
  error && typeof error === "object" && "data" in error
    ? (error as { data?: { error?: string } }).data?.error
    : undefined;

// RTK Query's fetchBaseQuery status is a real HTTP status code OR one of its own sentinel strings
// ("FETCH_ERROR" / "TIMEOUT_ERROR" / "PARSING_ERROR" / "CUSTOM_ERROR") for a request that never got
// a real response — never assume it's always numeric.
const getErrorStatus = (error: unknown): number | string | undefined =>
  error && typeof error === "object" && "status" in error
    ? (error as { status?: number | string }).status
    : undefined;

// Map the server's error codes to friendly messages. Falls back to a generic
// message when the shape is unknown (e.g. network error / 5xx).
export const mapPackageError = (error: unknown): string => {
  switch (getServerErrorCode(error)) {
    case "unknown_feature_key":
      return t("UNKNOWN_FEATURE_KEY_ERROR");
    case "duplicate_currency":
      return t("DUPLICATE_CURRENCY_ERROR");
    case "out_of_scope":
      return t("OUT_OF_SCOPE_ERROR");
    // Both size rules run on POST and PUT alike, so an edit that switches featureKey across the
    // token boundary can hit either of these where it used to save cleanly.
    case "credits_granted_required_for_token_pack":
      return t("CREDITS_GRANTED_REQUIRED_ERROR");
    case "amount_required":
      return t("AMOUNT_REQUIRED_ERROR");
    default:
      return t("SOMETHING_WENT_WRONG_TRY_AGAIN");
  }
};

// Map errors from the Buy-credits flow (FeatureCredits/Buy) to friendly messages. Covers every
// error code FeatureCreditsController can return; codes that get their own dedicated outcome UI
// (card_declined, no_saved_card, purchase_in_progress, charge_recorded_not_credited, 429,
// payment_provider_error — see classifyBuyOutcome) still resolve to a message here so the same
// function can back both that dedicated UI and any unexpected fallthrough.
export const mapBuyError = (error: unknown): string => {
  if (getErrorStatus(error) === 429) return t("BUY_THROTTLED_ERROR");

  switch (getServerErrorCode(error)) {
    case "out_of_scope":
      return t("OUT_OF_SCOPE_ERROR");
    case "no_price_for_currency":
      return t("BUY_NO_PRICE_FOR_CURRENCY_ERROR");
    case "currency_mismatch":
      return t("BUY_CURRENCY_MISMATCH_ERROR");
    case "invalid_scope_for_target":
      // Defensive only: the UI always sends scope "company" alongside
      // targetUserId, so this shouldn't fire from a correct client build.
      return t("BUY_INVALID_SCOPE_FOR_TARGET_ERROR");
    case "no_amount_due":
    case "invalid_package_amount":
      return t("BUY_SESSION_FAILED_ERROR");
    case "card_declined":
      return t("BUY_CARD_DECLINED_ERROR");
    case "no_saved_card":
      return t("BUY_NO_SAVED_CARD_ERROR");
    case "purchase_in_progress":
      return t("BUY_STILL_CONFIRMING_ERROR");
    case "charge_recorded_not_credited":
      return t("BUY_CAPTURED_NOT_CREDITED_ERROR");
    case "no_active_subscription":
      return t("BUY_STALE_ELIGIBILITY_ERROR");
    case "buyer_missing_contact":
      return t("BUY_BUYER_MISSING_CONTACT_ERROR");
    case "company_missing_billing_email":
      return t("MISSING_BILLING_EMAIL_MESSAGE");
    case "idempotency_key_conflict":
      return t("BUY_IDEMPOTENCY_KEY_CONFLICT_ERROR");
    case "package_not_found":
      return t("BUY_PACKAGE_NOT_FOUND_ERROR");
    case "payment_provider_error":
      return t("BUY_PROVIDER_UNAVAILABLE_ERROR");
    // card_not_accepted (stale-client guard), invalid_scope_for_seat, invalid_scope, cross_company
    // and unauthorized_role are all client-bug / defense-in-depth cases the UI should prevent from
    // ever firing — generic message rather than a dedicated one each.
    default:
      return getErrorStatus(error) === 403
        ? t("BUY_NOT_ALLOWED_ERROR")
        : t("SOMETHING_WENT_WRONG_TRY_AGAIN");
  }
};

// Map errors from cancelling a seat subscription. Far smaller than mapBuyError because Cancel makes
// no provider call — it is one local write that cannot half-land, so there is no unknown-outcome
// bucket to classify and nothing to retry with an idempotency key.
//
// 404 unknown_subscription means the row on screen is stale (already cancelled elsewhere, or the
// pack ended); the caller re-reads the list so the row disappears instead of inviting a second
// failing click.
export const mapSeatCancelError = (error: unknown): string => {
  if (getServerErrorCode(error) === "unknown_subscription") return t("SEAT_PACK_ALREADY_GONE");

  switch (getErrorStatus(error)) {
    case 429:
      return t("BUY_THROTTLED_ERROR");
    case 403:
      return t("BUY_NOT_ALLOWED_ERROR");
    default:
      return t("SOMETHING_WENT_WRONG_TRY_AGAIN");
  }
};

// What useFeatureCreditPurchase should do with the idempotency key and whether to auto-retry, for
// a failed Buy call. This is the one piece of real *logic* in the outcome table (the rest is copy,
// handled by mapBuyError above) — see "The idempotency key contract" in
// feture-purchase-payplus-flow.md §3 for the reasoning behind each bucket.
//
// The key is reused ONLY when the outcome is genuinely unknown: no response at all, a bare 500, a
// 502, or 409 purchase_in_progress. Every other definitive failure gets a new key before any next
// attempt — including ordinary 400s, which is easy to get backwards: a 400 raised *after* the
// purchase row already exists (company_missing_billing_email/no_amount_due via the settle path) is
// already bound to that row, so reusing the key would 409 instead of starting fresh.
export type BuyOutcomeAction =
  | "retry-same-key" // unknown outcome — bounded auto-retry with the same key, then reconciliation
  | "throttled" // 429, rejected at the edge before any row exists — same key, no auto-retry
  | "declined" // 402 card_declined — new key, manual retry
  | "conflict" // 400 idempotency_key_conflict — new key, exactly one automatic resubmit
  | "no-saved-card" // 402 no_saved_card — new key; dedicated CTA, see §6
  | "captured-not-credited" // 409 — money moved, never resubmit regardless of key
  | "stale" // 403 no_active_subscription — new key, refetch eligibility
  | "terminal"; // every other definitive failure — new key, generic mapped message

// Every field a Buy failure body might carry, read once so callers don't each re-implement the
// error?.data? unwrapping. purchaseId/paymentId/transactionUid/description are genuinely lowercase
// on the wire (anonymous C# objects in FeatureCreditsController, not a named/PascalCase DTO).
export interface BuyErrorData {
  code?: string;
  status?: number | string;
  purchaseId?: number;
  paymentId?: number | null;
  transactionUid?: string | null;
  description?: string;
}

export const getBuyErrorData = (error: unknown): BuyErrorData => {
  const data =
    error && typeof error === "object" && "data" in error
      ? (error as { data?: Record<string, unknown> }).data
      : undefined;

  return {
    code: getServerErrorCode(error),
    status: getErrorStatus(error),
    purchaseId: data?.purchaseId as number | undefined,
    paymentId: data?.paymentId as number | null | undefined,
    transactionUid: data?.transactionUid as string | null | undefined,
    description: data?.description as string | undefined,
  };
};

export const classifyBuyOutcome = (error: unknown): BuyOutcomeAction => {
  const { code, status } = getBuyErrorData(error);

  // Not a real HTTP response at all (RTK Query's FETCH_ERROR/TIMEOUT_ERROR/PARSING_ERROR/
  // CUSTOM_ERROR sentinels) or a bare 500 ({success:false,message}, no {error} code) — the charge's
  // outcome is genuinely unknown either way, same contract as purchase_in_progress: retry with the
  // same key and let the server's own idempotency check supply the real answer.
  if (typeof status !== "number" || status === 500) return "retry-same-key";

  if (status === 429) return "throttled";

  switch (code) {
    case "purchase_in_progress":
      return "retry-same-key";
    // 502: four distinct causes share this code (PaymentLinkSessionService's charge_exception,
    // customer_resolution_failed, token_list_failed, unknown_currency), and the client cannot tell
    // them apart — the body is only {error, purchaseId}, and ProviderDescription is never set on
    // this path. charge_exception alone may have actually reached the gateway, so this must get the
    // same treatment as a timeout rather than an assumption that nothing was charged.
    case "payment_provider_error":
      return "retry-same-key";
    case "card_declined":
      return "declined";
    case "idempotency_key_conflict":
      return "conflict";
    case "no_saved_card":
      return "no-saved-card";
    case "charge_recorded_not_credited":
      return "captured-not-credited";
    case "no_active_subscription":
      return "stale";
    default:
      // Every other definitive failure: any other 400 — including one with no {error} code at all
      // (e.g. a malformed idempotencyKey failing ASP.NET Core's own model validation before the
      // action runs, so nothing is bound to the key either) — plus a recurring
      // idempotency_key_conflict or an unrecognized 403. Nothing here should be retried blindly.
      return "terminal";
  }
};
